import React, { useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import EARNLogo from 'src/assets/images/earn-protocol.png'
import BLAZELogo from 'src/assets/images/titan-blaze.png'
import NFTLogo from 'src/assets/images/nft.png'
import YIELDLogo from 'src/assets/images/yield.png'
import LiquidLogo from 'src/assets/images/liquid-staking.png'
import LendingLogo from 'src/assets/images/lending.png'

const Projects = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const projects = [
    {
      title: 'EARN Protocol',
      subtitle: 'Decentralized Borrowing Protocol',
      description: 'A decentralized borrowing protocol within the PowerCity ecosystem that enables users to draw interest-free loans using crypto collateral. The system issues a USD-pegged stablecoin while maintaining protocol stability through overcollateralization, liquidation logic, and automated reward mechanisms.',
      image: EARNLogo,
      tags: ['DeFi', 'Lending', 'Stablecoin', 'LP Staking']
    },
    {
      title: 'Titan Blaze',
      subtitle: 'Token Minting and Staking',
      description: 'A token distribution and yield protocol that leverages daily on-chain auctions for fair price discovery and minting. Users participate using ETH to mint tokens from a decreasing daily supply, creating deflationary pressure through controlled emissions, staking incentives, and buy-and-burn mechanics.',
      image: BLAZELogo,
      tags: ['Auction', 'Staking', 'Tokenomics', 'Buy & Burn']
    },
    {
      title: 'Liquid Staking Protocol',
      subtitle: 'Liquid Staking & Yield Optimization',
      description: 'A liquid staking protocol that allows users to stake assets while retaining liquidity through derivative tokens. Participants earn continuous staking rewards without lockups, enabling capital efficiency and seamless integration across DeFi platforms such as lending, farming, and liquidity pools.',
      image: LiquidLogo,
      tags: ['BNB', 'Liquid Staking']
    },
    {
      title: 'DeFi Yield Aggregation',
      subtitle: 'DeFi Yield Aggregation Platform',
      description: 'A smart yield aggregation platform that automatically allocates funds across multiple DeFi strategies to optimize returns. The protocol handles strategy selection, compounding, and rebalancing, allowing users to access optimized yields through a simple, transparent, and non-custodial interface.',
      image: YIELDLogo,
      tags: ['DeFi', 'Yield Aggregation', 'Compounding']
    },
    {
      title: 'Lending & Borrowing Protocol',
      subtitle: 'Lending & Borrowing Protocol',
      description: 'A decentralized money market protocol that enables users to supply assets to earn interest or borrow against collateral. Built with algorithmic interest rates and automated liquidations, the system ensures capital efficiency, protocol solvency, and secure lending operations at scale.',
      image: LendingLogo,
      tags: ['DeFi', 'Lending', 'Borrowing']
    },
    {
      title: 'NFT Trading & Staking Protocol',
      subtitle: 'Decentralized NFT Marketplace',
      description: 'A fully on-chain NFT trading protocol supporting buying, selling, and staking of digital assets with instant settlement. The platform combines NFT liquidity with staking incentives and redistribution mechanics, delivering a scalable, low-cost marketplace with DeFi-style reward dynamics.',
      image: NFTLogo,
      tags: ['NFT', 'Marketplace', 'Staking']
    }
  ]

  const nextProject = () => {
    setCurrentIndex((prev) => (prev + 1) % projects.length);
  };

  const prevProject = () => {
    setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-[#0a0e1a] to-[#0f1420] relative">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Our Recent Projects
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            A curated selection of work showcasing blockchain & AI solutions we've delivered.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div
              key={index}
              className="group relative bg-gradient-to-br from-[#1a1f35] to-[#0f1420] rounded-2xl border border-white/10 overflow-hidden hover:border-white/30 transition-all duration-500 hover:transform hover:scale-[1.02]"
            >
              {/* Project Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f1420] to-transparent"></div>
              </div>

              {/* Project Content */}
              <div className="p-6 space-y-4">
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="px-3 py-1 bg-white/10 rounded-full text-xs text-gray-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h3 className="text-2xl font-bold text-white">{project.title}</h3>
                <p className="text-sm text-gray-400 font-medium">{project.subtitle}</p>
                <p className="text-gray-400 leading-relaxed">{project.description}</p>

                <Button className="w-full bg-gradient-to-r from-[#ff6b35] to-[#ff2e63] hover:from-[#ff7b45] hover:to-[#ff3e73] text-white py-2 rounded-lg flex items-center justify-center gap-2 group-hover:gap-3 transition-all duration-300">
                  Learn more <ArrowRight size={18} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;